import React, { useEffect, useRef } from 'react';
import { Button } from '@bumphealth/uxui-component-library';

import ExternalImage from '../product/ExternalImage';
import { reportUpgradeShown } from '../../containers/checkout-unified/analytics';

/* The premium upgrade's copy, as the prototype wrote it. It is fixed rather
   than read off the product record for the reason the prototype gave: the
   record's ownPromoData copy is the bundled-charge variant, not the
   free-first-month offer that is actually live. checkout-single-page's Start
   Membership pins its copy too (start-membership/index.js:929-1005), so both
   surfaces make one offer.

   None of this is a promise about what the shopper is charged. Accepting adds
   the product and the drawer re-reads the cart, so the line shows the server's
   amountToCharge and the order is priced at submit — the same division Start
   Membership makes, where the modal is copy and the money is the server's.

   The cost of fixing copy in code is that marketing cannot change it without a
   deploy, and that the $12.99 below has to move in step with the product. That
   is the trade Start Membership already makes; the premium upgrade refactor is
   where it gets revisited. */
// One place for the price both offers quote, so they cannot drift apart.
const UPGRADE_MONTHLY = 12.99;

const PREMIUM_UPGRADE = {
  eyebrow: 'One time offer',
  subHeadline: 'Then a bonus product in every box after that.',
  name: 'Premium Upgrade',
  // The markup appends the ®.
  brand: 'Bump Boxes',
  offerLabel: 'FREE First Month',
  priceNote: `then $${UPGRADE_MONTHLY.toFixed(2)}/mo`,
  buttonText: 'Yes! Upgrade my boxes',
  image:
    'https://bitsy-product-images.s3.amazonaws.com/online-store-images/bump-boxes/PremiumUpgrade_ModalPic',
  imageAlt: 'Premium Upgrade Modal Open Box',
};

/* A one-time offer has no fixed copy to stand in for it — the server decides
   what is offered, so the record is the only thing that knows. Legacy's popup
   reads ownPromoData unguarded, which is why it renders blank when a field is
   null; reading through a fallback costs the sentence, not the modal. */
const FALLBACK = {
  eyebrow: 'One time offer',
  buttonText: 'Add to my order',
  image:
    'https://s3.amazonaws.com/bitsy-product-images/default-product-image.png',
};

const DECLINE_TEXT = 'No thanks';

export const resolveContent = (promo) => {
  const own = promo?.ownPromoData || {};
  return {
    eyebrow: own.header || FALLBACK.eyebrow,
    headline: own.subHeader || null,
    subHeadline: own.promoDescription || null,
    name: promo?.name || '',
    brand: promo?.brand || '',
    // ownPromoData.cost is the offer price; actualCost is what it costs later.
    price: own.cost ?? promo?.actualCost ?? null,
    wasPrice: promo?.actualCost ?? null,
    buttonText: own.buttonText || FALLBACK.buttonText,
    image: promo?.media?.[0]?.url || FALLBACK.image,
    imageAlt: promo?.media?.[0]?.altTag || promo?.name || 'Special offer',
  };
};

const money = (amount) => `$${Number(amount).toFixed(2)}`;

/* A prepaid plan buys the upgrade the way it buys the boxes: every month of the
   term at checkout, nothing after (cart.lib.js:359-369 and models/cart.js:776-792
   charge months × cost and never reach the free-first-month override). There is
   no free first box to offer, so this copy does not offer one. */
const PREPAID_UPGRADE = {
  headline: 'Add a $30+ premium product to every box',
  offerLabel: `$${UPGRADE_MONTHLY.toFixed(2)} per box`,
  priceNote: 'charged today with your plan',
};

const prepaidLead = (months) =>
  `A bonus product in every box of your ${months}-month plan.`;

const PremiumUpgradeBody = ({ prepaidMonths }) => (
  <>
    <span className='coProto-modal__eyebrow'>{PREMIUM_UPGRADE.eyebrow}</span>
    <h2>
      {prepaidMonths ? (
        PREPAID_UPGRADE.headline
      ) : (
        <>
          Get a $30+ premium product{' '}
          <span className='coProto-modal__accent'>FREE</span> in your 1st box
        </>
      )}
    </h2>
    <p className='coProto-modal__lead'>
      {prepaidMonths
        ? prepaidLead(prepaidMonths)
        : PREMIUM_UPGRADE.subHeadline}
    </p>

    <div className='coProto-modal__product'>
      <div className='coProto-modal__media'>
        <ExternalImage
          src={PREMIUM_UPGRADE.image}
          alt={PREMIUM_UPGRADE.imageAlt}
          sizes='(min-width: 992px) 240px, 45vw'
        />
      </div>

      <div className='coProto-modal__detail'>
        <h3>{PREMIUM_UPGRADE.name}</h3>
        <span className='coProto-modal__brand'>{PREMIUM_UPGRADE.brand}®</span>
        <span className='coProto-modal__offer'>
          {prepaidMonths
            ? PREPAID_UPGRADE.offerLabel
            : PREMIUM_UPGRADE.offerLabel}
        </span>
        <span className='coProto-modal__then'>
          {prepaidMonths ? PREPAID_UPGRADE.priceNote : PREMIUM_UPGRADE.priceNote}
        </span>
      </div>
    </div>
  </>
);

const OfferBody = ({ content }) => {
  const isDiscounted =
    content.price !== null && content.wasPrice > content.price;

  return (
    <>
      <span className='coProto-modal__eyebrow'>{content.eyebrow}</span>
      {content.headline ? <h2>{content.headline}</h2> : null}
      {content.subHeadline ? (
        <p className='coProto-modal__lead'>{content.subHeadline}</p>
      ) : null}

      <div className='coProto-modal__product'>
        <div className='coProto-modal__media'>
          <ExternalImage
            src={content.image}
            alt={content.imageAlt}
            sizes='(min-width: 992px) 240px, 45vw'
          />
        </div>

        <div className='coProto-modal__detail'>
          {content.name ? <h3>{content.name}</h3> : null}
          {content.brand ? (
            <span className='coProto-modal__brand'>{content.brand}®</span>
          ) : null}

          {/* One price, struck through against what it normally costs — the
              same pair legacy's popup shows. */}
          {content.price !== null ? (
            <span className='coProto-modal__offer'>
              {content.price === 0 ? 'FREE' : money(content.price)}
            </span>
          ) : null}
          {isDiscounted ? (
            <span className='coProto-modal__value'>
              <s>{money(content.wasPrice)}</s>
            </span>
          ) : null}
        </div>
      </div>
    </>
  );
};

const PreCheckoutOfferModal = ({
  isOpen,
  promo,
  isRecurring,
  prepaidMonths,
  onAccept,
  onDecline,
  isBusy,
}) => {
  /* Reported from here, once per opening, because this is what shows it. Only
     the upgrade has 2024 events to match; the one-time promo has none. */
  const reported = useRef(false);
  useEffect(() => {
    if (!isOpen) {
      reported.current = false;
      return;
    }
    if (reported.current || !isRecurring) return;
    reported.current = true;
    reportUpgradeShown();
  }, [isOpen, isRecurring]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      // Both exits report, so an accept already in flight would be declined too.
      if (e.key === 'Escape' && !isBusy) onDecline();
    };
    document.addEventListener('keydown', onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [isOpen, isBusy, onDecline]);

  if (!isOpen) return null;

  const content = isRecurring ? PREMIUM_UPGRADE : resolveContent(promo);

  return (
    <div className='coProto-modal'>
      <button
        type='button'
        className='coProto-modal__scrim'
        onClick={onDecline}
        disabled={isBusy}
        aria-label='Close'
      />
      <div
        className='coProto-modal__card'
        role='dialog'
        aria-modal='true'
        aria-label={content.name || 'Special offer'}
      >
        {isRecurring ? (
          <PremiumUpgradeBody prepaidMonths={prepaidMonths} />
        ) : (
          <OfferBody content={content} />
        )}

        <Button
          variant='primary'
          fullWidth
          className='coProto-cta'
          onClick={onAccept}
          disabled={isBusy}
        >
          {isBusy ? 'Adding…' : content.buttonText}
        </Button>
        <button
          type='button'
          className='coProto-modal__decline'
          onClick={onDecline}
          disabled={isBusy}
        >
          {DECLINE_TEXT}
        </button>
      </div>
    </div>
  );
};

export default PreCheckoutOfferModal;
